/**
 * ScoreEntry.js - Score entry page for a league
 */

import React, { useState, useEffect } from 'react';
import * as api from '../services/api';
import { formatDate } from '../utils/dateFormat';
import { getSportConfig } from '../utils/sportConfig';
import './ScoreEntry.css';

const statusColors = {
  scheduled: '#fff',
  completed: '#f8fdf8',
  postponed: '#fff3cd',
  cancelled: '#f8d7da',
  forfeit: '#ffe0b2'
};

function GameRow({ game, teamMap, scores, onScoreChange, onSave, onStatusChange, saving, sportConfig }) {
  const gameScores = scores[game.id] || {};
  const team1Name = teamMap[game.team1Id] || 'Team ?';
  const team2Name = teamMap[game.team2Id] || 'Team ?';
  const gameStatus = game.status || 'scheduled';
  const isScoreable = gameStatus === 'scheduled' || gameStatus === 'completed';
  const hasScores = gameScores.team1Score != null && gameScores.team1Score !== '' &&
                    gameScores.team2Score != null && gameScores.team2Score !== '';
  const isSaved = game.played && gameStatus === 'completed' &&
                  game.team1Score === gameScores.team1Score &&
                  game.team2Score === gameScores.team2Score;
  const isTied = hasScores && gameScores.team1Score === gameScores.team2Score;
  const tieBlocked = isTied && !sportConfig.allowTies;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      borderBottom: '1px solid #f0f0f0',
      background: statusColors[gameStatus] || 'white'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '0.4rem 0.5rem',
        gap: '0.5rem',
      }}>
        <span style={{ color: '#888', fontSize: '0.85rem', flexShrink: 0, width: '42px' }}>{game.time || '--'}</span>
        <select
          value={gameStatus}
          onChange={(e) => onStatusChange(game, e.target.value)}
          disabled={saving[game.id]}
          style={{
            padding: '0.15rem 0.2rem',
            borderRadius: 4,
            border: '1px solid #d0d0d0',
            fontSize: '0.7rem',
            width: 82,
            flexShrink: 0,
            background: statusColors[gameStatus] || 'white',
            cursor: 'pointer'
          }}
        >
          <option value="scheduled">Scheduled</option>
          <option value="completed">Completed</option>
          <option value="postponed">Postponed</option>
          <option value="cancelled">Cancelled</option>
          <option value="forfeit">Forfeit</option>
        </select>
        {isScoreable && sportConfig.winLossOnly ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flex: 1, minWidth: 0 }}>
            <select
              value={
                gameScores.team1Score === 1 && gameScores.team2Score === 0 ? 'team1' :
                gameScores.team1Score === 0 && gameScores.team2Score === 1 ? 'team2' :
                gameScores.team1Score === 0 && gameScores.team2Score === 0 && (gameScores.team1Score != null && gameScores.team1Score !== '') ? 'draw' : ''
              }
              onChange={(e) => {
                const val = e.target.value;
                if (val === 'team1') { onScoreChange(game.id, 'team1Score', '1'); onScoreChange(game.id, 'team2Score', '0'); }
                else if (val === 'team2') { onScoreChange(game.id, 'team1Score', '0'); onScoreChange(game.id, 'team2Score', '1'); }
                else if (val === 'draw') { onScoreChange(game.id, 'team1Score', '0'); onScoreChange(game.id, 'team2Score', '0'); }
              }}
              style={{ padding: '0.25rem', borderRadius: 4, border: '1px solid #d0d0d0', fontSize: '0.85rem', flex: 1 }}
            >
              <option value="">Select result...</option>
              <option value="team1">{team1Name} wins</option>
              <option value="team2">{team2Name} wins</option>
              <option value="draw">Draw</option>
            </select>
          </div>
        ) : isScoreable ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flex: 1, minWidth: 0 }}>
            <span style={{ fontWeight: 500, color: '#333', flex: '1 1 0', textAlign: 'right', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{team1Name}</span>
            <input
              type="number"
              min="0"
              title={sportConfig.scoreLabel}
              style={{
                width: '40px',
                flexShrink: 0,
                padding: '0.25rem',
                textAlign: 'center',
                border: `2px solid ${tieBlocked ? '#e53e3e' : '#e0e0e0'}`,
                borderRadius: '4px',
                fontSize: '0.9rem',
                fontWeight: 600
              }}
              value={gameScores.team1Score ?? ''}
              onChange={(e) => onScoreChange(game.id, 'team1Score', e.target.value)}
              placeholder="-"
            />
            <span style={{ fontWeight: 600, color: '#888' }}>-</span>
            <input
              type="number"
              min="0"
              title={sportConfig.scoreLabel}
              style={{
                width: '40px',
                flexShrink: 0,
                padding: '0.25rem',
                textAlign: 'center',
                border: `2px solid ${tieBlocked ? '#e53e3e' : '#e0e0e0'}`,
                borderRadius: '4px',
                fontSize: '0.9rem',
                fontWeight: 600
              }}
              value={gameScores.team2Score ?? ''}
              onChange={(e) => onScoreChange(game.id, 'team2Score', e.target.value)}
              placeholder="-"
            />
            <span style={{ fontWeight: 500, color: '#333', flex: '1 1 0', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{team2Name}</span>
          </div>
        ) : gameStatus === 'forfeit' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: '0.8rem', color: '#e65100', fontWeight: 600, marginRight: '0.3rem' }}>Forfeited by:</span>
            <select
              value={game.forfeitTeamId || ''}
              onChange={(e) => onStatusChange(game, 'forfeit', { forfeitTeamId: e.target.value })}
              disabled={saving[game.id]}
              style={{ padding: '0.2rem', borderRadius: 4, border: '1px solid #d0d0d0', fontSize: '0.85rem', flex: 1 }}
            >
              <option value="">Select team...</option>
              <option value={game.team1Id}>{team1Name}</option>
              <option value={game.team2Id}>{team2Name}</option>
            </select>
          </div>
        ) : gameStatus === 'postponed' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: '0.8rem', color: '#856404', fontWeight: 600, whiteSpace: 'nowrap' }}>Reschedule:</span>
            <input
              type="date"
              value={game.rescheduledDate || ''}
              onChange={(e) => onStatusChange(game, 'postponed', { rescheduledDate: e.target.value, rescheduledTime: game.rescheduledTime })}
              style={{ padding: '0.15rem', borderRadius: 4, border: '1px solid #d0d0d0', fontSize: '0.8rem', flex: 1 }}
            />
            <input
              type="text"
              placeholder="Time"
              value={game.rescheduledTime || ''}
              onChange={(e) => onStatusChange(game, 'postponed', { rescheduledDate: game.rescheduledDate, rescheduledTime: e.target.value })}
              style={{ padding: '0.15rem', borderRadius: 4, border: '1px solid #d0d0d0', fontSize: '0.8rem', width: 70 }}
            />
          </div>
        ) : gameStatus === 'cancelled' ? (
          <div style={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'center' }}>
            <span style={{ color: '#dc3545', fontWeight: 600, fontSize: '0.85rem' }}>CANCELLED</span>
          </div>
        ) : null}
        <div style={{ flexShrink: 0, minWidth: '40px', textAlign: 'center' }}>
          {isScoreable && isSaved ? (
            <span style={{ color: '#2e7d32', fontSize: '1.1rem', fontWeight: 700 }} title="Saved">&#10003;</span>
          ) : isScoreable ? (
            <button
              style={{
                background: '#667eea',
                color: 'white',
                border: 'none',
                padding: '0.25rem 0.75rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 500,
                opacity: (!hasScores || saving[game.id] || tieBlocked) ? 0.5 : 1
              }}
              onClick={() => onSave(game)}
              disabled={!hasScores || saving[game.id] || tieBlocked}
            >
              {saving[game.id] ? '...' : 'Save'}
            </button>
          ) : saving[game.id] ? (
            <span style={{ color: '#888', fontSize: '0.8rem' }}>...</span>
          ) : null}
        </div>
      </div>
      {isScoreable && tieBlocked && (
        <div style={{ padding: '0 0.5rem 0.4rem 0.5rem', color: '#e53e3e', fontSize: '0.75rem', textAlign: 'center' }}>
          Ties are not allowed in {sportConfig.label}
        </div>
      )}
    </div>
  );
}

function ScoreEntry({ leagueId, onBack }) {
  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [scores, setScores] = useState({});
  const [saving, setSaving] = useState({});

  useEffect(() => {
    loadLeague();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId]);

  const showToast = (message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadLeague = async () => {
    try {
      setLoading(true);
      const response = await api.getLeague(leagueId);
      const leagueData = response.league;
      setLeague(leagueData);

      // Initialize scores from existing game data
      const initialScores = {};
      (leagueData.games || []).forEach(game => {
        if (game.played) {
          initialScores[game.id] = {
            team1Score: game.team1Score,
            team2Score: game.team2Score
          };
        }
      });
      setScores(initialScores);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleScoreChange = (gameId, field, value) => {
    const numValue = value === '' ? '' : parseInt(value, 10);
    if (value !== '' && (isNaN(numValue) || numValue < 0)) return;
    setScores(prev => ({
      ...prev,
      [gameId]: {
        ...prev[gameId],
        [field]: numValue
      }
    }));
  };

  const handleSave = async (game) => {
    const gameScores = scores[game.id];
    if (!gameScores || gameScores.team1Score === '' || gameScores.team2Score === '' ||
        gameScores.team1Score == null || gameScores.team2Score == null) {
      showToast('Please enter both scores');
      return;
    }

    setSaving(prev => ({ ...prev, [game.id]: true }));
    try {
      await api.updateGameScore(leagueId, game.id, gameScores.team1Score, gameScores.team2Score);

      setLeague(prev => ({
        ...prev,
        games: prev.games.map(g =>
          g.id === game.id
            ? { ...g, team1Score: gameScores.team1Score, team2Score: gameScores.team2Score, played: true, status: 'completed' }
            : g
        )
      }));

      showToast('Score saved!', 'success');
    } catch (err) {
      showToast('Failed to save score: ' + err.message);
    } finally {
      setSaving(prev => ({ ...prev, [game.id]: false }));
    }
  };

  const handleStatusChange = async (game, newStatus, extra = {}) => {
    // If changing to forfeit without a team selected yet, just update local state
    if (newStatus === 'forfeit' && extra.forfeitTeamId === undefined && !game.forfeitTeamId) {
      // Just set the dropdown to forfeit — user still needs to pick a team
      setLeague(prev => ({
        ...prev,
        games: prev.games.map(g =>
          g.id === game.id ? { ...g, status: 'forfeit' } : g
        )
      }));
      return;
    }

    // For forfeit, require a team to be selected before calling API
    if (newStatus === 'forfeit' && !extra.forfeitTeamId) {
      return;
    }

    setSaving(prev => ({ ...prev, [game.id]: true }));
    try {
      const data = { status: newStatus, ...extra };
      const result = await api.updateGameStatus(leagueId, game.id, data);
      const updatedGame = result.game;

      setLeague(prev => ({
        ...prev,
        games: prev.games.map(g =>
          g.id === game.id
            ? {
                ...g,
                status: updatedGame.status,
                played: updatedGame.played,
                team1Score: updatedGame.team1Score,
                team2Score: updatedGame.team2Score,
                forfeitTeamId: updatedGame.forfeitTeamId,
                forfeitTeam: updatedGame.forfeitTeam,
                rescheduledDate: updatedGame.rescheduledDate,
                rescheduledTime: updatedGame.rescheduledTime
              }
            : g
        )
      }));

      // Update local scores state if status cleared scores
      if (updatedGame.team1Score == null) {
        setScores(prev => {
          const next = { ...prev };
          delete next[game.id];
          return next;
        });
      }

      const labels = { scheduled: 'Reset to scheduled', completed: 'Marked as completed', postponed: 'Game postponed', cancelled: 'Game cancelled', forfeit: 'Forfeit recorded' };
      showToast(labels[newStatus] || 'Status updated', 'success');
    } catch (err) {
      showToast('Failed: ' + err.message);
      // Revert local status on error
      loadLeague();
    } finally {
      setSaving(prev => ({ ...prev, [game.id]: false }));
    }
  };

  if (loading) {
    return (
      <div className="score-entry-page">
        <div className="score-loading">Loading games...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="score-entry-page">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  // Build team name lookup
  const teamMap = {};
  (league.teams || []).forEach(t => { teamMap[t.id] = t.name; });

  const sportConfig = getSportConfig(league.sport);

  // Group games by week
  const gamesByWeek = {};
  (league.games || []).forEach(game => {
    if (!gamesByWeek[game.week]) {
      gamesByWeek[game.week] = { week: game.week, date: game.date, games: [] };
    }
    gamesByWeek[game.week].games.push(game);
  });
  const weeks = Object.values(gamesByWeek).sort((a, b) => a.week - b.week);

  return (
    <div className="score-entry-page">
      <div className="score-header">
        <div>
          <h1>Enter {sportConfig.winLossOnly ? 'Results' : sportConfig.scoreLabel === 'Score' ? 'Scores' : sportConfig.scoreLabel}</h1>
          <p className="score-league-name">{league.name} - {league.seasonYear}</p>
        </div>
      </div>

      {toast && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
          <button className="toast-close" onClick={() => setToast(null)}>x</button>
        </div>
      )}

      {weeks.length === 0 && (
        <p style={{ color: '#666', padding: '2rem', textAlign: 'center' }}>No games found for this league.</p>
      )}

      <div className="score-weeks-list">
        {weeks.map(week => (
          <div key={week.week} className="score-week-card">
            <div className="score-week-header">
              <h3>Week {week.week}</h3>
              <span className="score-week-date">{formatDate(week.date)} ({week.games.length} games)</span>
            </div>
            <div className="score-games">
              {week.games.map(game => (
                <GameRow
                  key={game.id}
                  game={game}
                  teamMap={teamMap}
                  scores={scores}
                  onScoreChange={handleScoreChange}
                  onSave={handleSave}
                  onStatusChange={handleStatusChange}
                  saving={saving}
                  sportConfig={sportConfig}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ScoreEntry;
